import { type CountryCode } from "@/config/country-config";
import { type CountryRules, getCountryRules } from "@/config/country-rules";
import { isAutofillEnabled } from "@/dev/console/autofill-preference";
import type { CreditCardCustomerForm } from "@/features/customer-information/hooks/use-credit-card-customer-form";
import type { LoanCustomerForm } from "@/features/customer-information/hooks/use-loan-customer-form";
import type { MyMoneyCustomerForm } from "@/features/customer-information/hooks/use-my-money-customer-form";
import type { InsuranceForm } from "@/features/insurance-sale/hooks/use-insurance-form";
import {
	ExpectedLocationUseOfCard,
	ExpectedUseOfCard,
	HowToPayForInvoices,
} from "@/types/form";

const citizenshipFor = (country: CountryCode): string[] => [country];

/**
 * An IBAN for the Finnish scheme. The Swedish-scheme presets always supply a
 * trusted account, so that form shows the single combined clearing + account
 * field and autofill has to match it: a whole domestic number, no clearing.
 */
const MOCK_ACCOUNT_NUMBER: Record<CountryRules["payoutAccount"], string> = {
	"swedish": "12341234567",
	"finnish-iban": "FI21 1234 5600 0007 85",
};

const accountNumberFor = (country: CountryCode): string =>
	MOCK_ACCOUNT_NUMBER[getCountryRules(country).payoutAccount];

const dueDateFor = (country: CountryCode): string =>
	getCountryRules(country).dueDateChoice ? "1" : "";

/** Merge mock defaults on top of mapped context values; `mock` is `useRuntimeConfig().mock`. */
export const applyMockFormDefaults = <T extends object>(
	values: Partial<T>,
	mockPatch: Partial<T>,
	mock: boolean
): T =>
	(mock && isAutofillEnabled() ? { ...values, ...mockPatch } : values) as T;

export const getMockLoanCustomerDefaults = (
	country: CountryCode,
	showLoanPurposeSelect = false,
	askNumberOfCars = false
): Partial<LoanCustomerForm> => {
	return {
		isPEP: false,
		citizenship: citizenshipFor(country),
		clearingNumber: "",
		accountNumber: accountNumberFor(country),
		dueDate: dueDateFor(country),
		loanPurpose: showLoanPurposeSelect ? "Education" : "",
		consent: true,
		numberOfCars: askNumberOfCars ? 1 : null,
	};
};

export const getMockMyMoneyCustomerDefaults = (
	country: CountryCode,
	showLoanPurposeSelect = false,
	askNumberOfCars = false
): Partial<MyMoneyCustomerForm> => {
	return {
		isPEP: false,
		citizenship: citizenshipFor(country),
		clearingNumber: "",
		accountNumber: accountNumberFor(country),
		dueDate: dueDateFor(country),
		loanPurpose: showLoanPurposeSelect ? "Education" : "",
		autogiroConsent: true,
		payoutToMyMoneyConsent: true,
		numberOfCars: askNumberOfCars ? 1 : null,
	};
};

export const getMockCreditCardCustomerDefaults =
	(): Partial<CreditCardCustomerForm> => ({
		isPEP: false,
		citizenship: ["SE"],
		expectedUseOfCard: [ExpectedUseOfCard.PersonalExpenses],
		expectedLocationUseOfCard: [ExpectedLocationUseOfCard.Europe],
		howToPayForInvoices: [HowToPayForInvoices.Salary],
		useCardForTransactions: false,
		withdrawMoreThan50k: false,
	});

export const getMockInsuranceDefaults = (): Partial<InsuranceForm> => ({
	insurance: false,
	termsConsent: false,
});
